import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { fadeIn } from '../../utils/animations';

interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'outline' | 'text';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  disabled?: boolean;
}

// Кнопки внутри <form> по умолчанию получают type="submit" и незаметно отправляют форму.
// Здесь дефолт — type="button"; для реального submit нужно явно указать type="submit".
export const Button = styled.button.attrs<ButtonProps>((props) => ({
  type: props.type ?? 'button',
}))<ButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ size, theme }) => {
    switch (size) {
      case 'sm': return `calc(${theme.spacing.xs} * var(--card-scale, 1)) calc(${theme.spacing.md} * var(--card-scale, 1))`;
      case 'lg': return `calc(${theme.spacing.md} * var(--card-scale, 1)) calc(${theme.spacing.xl} * var(--card-scale, 1))`;
      default: return `calc(${theme.spacing.sm} * var(--card-scale, 1)) calc(${theme.spacing.lg} * var(--card-scale, 1))`;
    }
  }};
  font-size: calc(${({ size }) => (size === 'sm' ? '14px' : size === 'lg' ? '18px' : '16px')} * var(--card-scale, 1));
  font-weight: 600;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: all ${({ theme }) => theme.transitions.normal};
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  animation: ${fadeIn} 0.3s ease-out;

  ${({ variant, theme }) => {
    switch (variant) {
      case 'primary':
        return `
          background-color: #7F7679;
          color: #ffffff;
          box-shadow: ${theme.shadows.md};
          
          &:hover:not(:disabled) {
            background-color: #6f676a;
            transform: translateY(-2px);
            box-shadow: ${theme.shadows.lg};
          }
          
          &:active:not(:disabled) {
            transform: translateY(0);
          }
        `;
      case 'secondary':
        return `
          background-color: ${theme.colors.secondary};
          color: white;
          box-shadow: ${theme.shadows.md};
          
          &:hover:not(:disabled) {
            background-color: ${theme.colors.secondaryDark};
            transform: translateY(-2px);
            box-shadow: ${theme.shadows.lg};
          }
        `;
      case 'outline':
        return `
          background-color: transparent;
          border: 2px solid ${theme.colors.primary};
          color: ${theme.colors.primary};
          
          &:hover:not(:disabled) {
            background-color: ${theme.colors.primary};
            color: white;
          }
        `;
      case 'text':
        return `
          background-color: transparent;
          color: ${theme.colors.textLight};
          
          &:hover:not(:disabled) {
            color: ${theme.colors.text};
            background-color: ${theme.colors.background};
          }
        `;
      default:
        return `
          background-color: #7F7679;
          color: #ffffff;
          box-shadow: ${theme.shadows.md};
          
          &:hover:not(:disabled) {
            background-color: #6f676a;
            transform: translateY(-2px);
            box-shadow: ${theme.shadows.lg};
          }
        `;
    }
  }}

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 16px;
    padding: ${({ size, theme }) => {
      switch (size) {
        case 'sm': return `${theme.spacing.xs} ${theme.spacing.lg}`;
        case 'lg': return `${theme.spacing.md} ${theme.spacing.xxl}`;
        default: return `${theme.spacing.sm} ${theme.spacing.xl}`;
      }
    }};
  }
`;
